// ============================================
// pages/EditProfile.tsx
// 个人资料编辑: 昵称 + 简介 + 头像(DiceBear 预设选择).
// 昵称/头像写回 app_users(双向同步), 简介存创新工场档.
// ============================================

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import LoadingSpinner from '../components/LoadingSpinner';
import Avatar from '../components/Avatar';
import { callApi } from '../services/api';
import { getCachedUser, setCachedUser } from '../services/auth';

interface PrivateProfile {
  user_id: string;
  short_id: string;
  nickname: string | null;
  avatar_url: string | null;
  bio: string;
}

export default function EditProfile() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  const [nickname, setNickname] = useState('');
  const [bio, setBio] = useState('');
  const [shortId, setShortId] = useState('');

  useEffect(() => {
    callApi<{ profile: PrivateProfile }>('community-get-my-profile', {})
      .then((r) => {
        if (r.ok && r.profile) {
          const p = r.profile as PrivateProfile;
          setNickname(p.nickname || '');
          setBio(p.bio || '');
          setShortId(p.short_id || '');
        } else {
          setError(r.message || r.error || '加载失败');
        }
      })
      .catch(() => setError('加载失败'))
      .finally(() => setLoading(false));
  }, []);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  };

  const save = async () => {
    if (!nickname.trim()) { showToast('昵称不能为空'); return; }
    setSaving(true);
    const r = await callApi<{ profile: PrivateProfile }>('community-update-my-profile', {
      nickname: nickname.trim(),
      bio,
    });
    setSaving(false);
    if (r.ok) {
      // 更新本地缓存昵称, 顶栏等立即生效
      const cached = getCachedUser();
      if (cached) setCachedUser({ ...cached, nickname: nickname.trim() });
      showToast('已保存 ✓');
      setTimeout(() => navigate(`/u/${shortId}`), 800);
    } else {
      showToast(r.message || r.error || '保存失败');
    }
  };

  if (loading) {
    return <Layout><div className="py-24 flex justify-center"><LoadingSpinner hint="加载资料…" /></div></Layout>;
  }
  if (error) {
    return <Layout><div className="py-24 text-center text-red-600">{error}</div></Layout>;
  }

  return (
    <Layout>
      <div className="max-w-xl mx-auto px-4 py-6 space-y-6">
        <h1 className="text-xl font-black text-ink-900">编辑资料</h1>

        {/* 头像 = 昵称首字自动生成(无需上传/选择) */}
        <div className="flex items-center gap-4">
          <Avatar name={nickname} size={72} />
          <div className="text-sm text-ink-500">头像由昵称首字自动生成</div>
        </div>

        {/* 昵称 */}
        <div>
          <label className="block text-sm font-medium text-ink-700 mb-1.5">昵称</label>
          <input
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            maxLength={30}
            placeholder="给自己起个名字"
            className="w-full px-3 py-2.5 border border-ink-300/60 rounded-xl text-sm"
          />
          <p className="text-xs text-ink-400 mt-1">昵称在 App 和创新工场通用,改了两边都更新</p>
        </div>

        {/* 简介 */}
        <div>
          <label className="block text-sm font-medium text-ink-700 mb-1.5">个人简介</label>
          <textarea
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            maxLength={200}
            rows={3}
            placeholder="介绍一下自己吧"
            className="w-full px-3 py-2.5 border border-ink-300/60 rounded-xl text-sm resize-none"
          />
        </div>

        {/* 操作 */}
        <div className="flex gap-3">
          <button
            onClick={save}
            disabled={saving}
            className="px-6 py-2.5 rounded-xl bg-gold-600 text-white font-medium text-sm hover:bg-gold-700 disabled:opacity-60"
          >
            {saving ? '保存中…' : '保存'}
          </button>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-2.5 rounded-xl bg-ink-100 text-ink-600 font-medium text-sm hover:bg-ink-200"
          >
            取消
          </button>
        </div>

        {toast && (
          <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 px-5 py-2.5 rounded-xl bg-ink-900/90 text-white text-sm font-medium shadow-lg">
            {toast}
          </div>
        )}
      </div>
    </Layout>
  );
}
